import { FileText, ExternalLink } from "lucide-react";
import { resolveImageSrc } from "@/lib/api";

/**
 * MessageAttachmentList — renders below a message bubble. Images inline
 * (thumbnail + click-to-open), PDFs as a download-style pill link.
 *
 * `mine` prop controls color palette so the treatment blends into the
 * sender-side bubble color (organizer messages are purple bubbles;
 * matched vendor messages are sand bubbles).
 */
export default function MessageAttachmentList({ attachments, mine = false }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={`mt-1.5 flex flex-wrap gap-1.5 ${mine ? "justify-end" : "justify-start"}`}
         data-testid="attachment-list">
      {attachments.map((a, i) => {
        const isImage = (a.content_type || "").startsWith("image/");
        const abs = resolveImageSrc(a.url);
        return isImage
          ? <ImageAttachment key={a.url + i} attachment={a} src={abs} testId={`attachment-image-${i}`} />
          : <PdfAttachment key={a.url + i} attachment={a} src={abs} mine={mine} testId={`attachment-pdf-${i}`} />;
      })}
    </div>
  );
}

function ImageAttachment({ attachment, src, testId }) {
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-md overflow-hidden border"
      style={{ borderColor: "var(--border-warm)", maxWidth: 220 }}
      data-testid={testId}
    >
      <img
        src={src}
        alt={attachment.filename || "attachment"}
        className="block"
        style={{ maxWidth: 220, maxHeight: 180, objectFit: "cover" }}
      />
    </a>
  );
}

function PdfAttachment({ attachment, src, mine, testId }) {
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border"
      style={{
        backgroundColor: mine ? "rgba(255,255,255,0.15)" : "var(--paper-warm)",
        borderColor: mine ? "rgba(255,255,255,0.25)" : "var(--border-warm)",
        color: mine ? "#fff" : "var(--ink)",
      }}
      data-testid={testId}
    >
      <FileText className="h-3.5 w-3.5" />
      <span className="max-w-[180px] truncate">{attachment.filename || "document.pdf"}</span>
      <ExternalLink className="h-3 w-3 opacity-70" />
    </a>
  );
}
